import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import ComercialDrawer from "../../../../components/general/ComercialDrawer";
import { PrimaryButton } from "../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../components/general/buttons/SecondaryButton";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FoodService } from "../../../../types/types";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import GalleryImageList from "../../../../components/comercial/ImageGallery";
import dayjs, { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { excursionValidationSchemas } from "../excursionValidationSchemas";
import PriceInput from "../../../../components/general/PriceInput";
import { IStore } from "../../../../services/api/Store/types";
import { getLocalStorageItem } from "../../../../hooks/localStorageHooks";
import { sharedStyles } from "../../../../styles/shared";
import ImageGalleryContainer from "../../../../components/comercial/ImageGallery/ImageGalleryContainer";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import Tiptap from "@/components/general/Tiptap";
import EmptyGallery from "@/components/comercial/ImageGallery/EmptyGallery";
import ExcursionRouter from "@/components/general/ExcursionRouter";
import { handleNextStep, handlePreviousStep } from "../../sharedFunctions";
import IExcursion from "@/services/api/Excursion/IExcursion";
import { ObjectSchema } from "yup";
import AddImageInput from "@/components/comercial/AddImageInput";
import DateSelector from "@/components/general/DateSelector";
import AddIcon from "@mui/icons-material/Add";
import { EXCURSION_OPERATION_STEPS, handleCreateExcursion } from "./functions";

const AddExcursion = () => {
  const navigate = useNavigate();

  const selectedAgency: IStore = getLocalStorageItem("selectedStore");

  const [images, setImages] = useState<HTMLCanvasElement[]>([]);

  const descriptionRef = useRef("");
  const [router, setRouter] = useState([]);

  const [price, setPrice] = useState(0);

  const [startDate, setStartDate] = useState<Dayjs | null>(() =>
    dayjs().add(1, "day")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(() =>
    startDate.add(1, "day")
  );
  const [error, setError] = useState<string | null>(null);

  const [additionStep, setAdditionStep] = useState(0);
  const [additionLoading, setAdditionLoading] = useState(false);

  const isLastAdditionStep =
    additionStep === EXCURSION_OPERATION_STEPS.length - 1;
  const endDateIsEarlierThanStart = endDate < startDate;

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(
      excursionValidationSchemas[additionStep] as ObjectSchema<
        Partial<IExcursion>
      >
    ),
  });

  const formValues = getValues();

  const handleSubmitAddition = () => {
    if (isLastAdditionStep) {
      setAdditionLoading(true);

      handleCreateExcursion(
        {
          storeId: selectedAgency.id,
          title: formValues.title,
          price: price * 100,
          mainCategory: formValues.mainCategory,
          agent: formValues.agent,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          minVacancies: Number(formValues.minVacancies),
          maxVacancies: Number(formValues.maxVacancies),
          description: descriptionRef.current,
          router: router,
          transportService: formValues.transportService,
          accommodationService: formValues.accommodationService,
          foodService: formValues.foodService,
          excursionContract: formValues.excursionContract,
          groupLink: formValues.groupLink,
          city: formValues.city,
          state: formValues.state,
          region: formValues.region,
          country: formValues.country,
          images: images,
        },
        selectedAgency.name
      )
        .then((response) => {
          alert(`Excursão ${response.title} criada com sucesso!`);
          navigate("/excursions");
        })
        .catch((error) => {
          setError(error.response.data.message);
          setAdditionStep(0);
        })
        .finally(() => setAdditionLoading(false));
    } else if (additionStep !== 0 || !endDateIsEarlierThanStart)
      handleNextStep(
        additionStep,
        setAdditionStep,
        EXCURSION_OPERATION_STEPS.length
      );
  };

  const renderExcursionData = (
    <>
      <Box sx={sharedStyles.verticalContent}>
        <TextField
          label="Título *"
          size="small"
          fullWidth
          {...register("title")}
          error={!!errors.title}
          helperText={errors.title?.message}
          autoComplete="off"
        />

        <PriceInput
          label="Preço (R$) "
          placeholder="Ex.: R$ 500,00"
          autoComplete="off"
          fullWidth
          value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          required
        />

        <Box sx={sharedStyles.horizontalContent}>
          <TextField
            label="Categoria *"
            size="small"
            fullWidth
            {...register("mainCategory")}
            error={!!errors.mainCategory}
            helperText={errors.mainCategory?.message}
            autoComplete="off"
          />

          <TextField
            label="Agente *"
            size="small"
            fullWidth
            {...register("agent")}
            error={!!errors.agent}
            helperText={errors.agent?.message}
            autoComplete="off"
          />
        </Box>

        <Box sx={sharedStyles.horizontalContent}>
          <DateSelector
            label="Data de início *"
            value={startDate}
            setValue={setStartDate}
            size="medium"
            disablePast
          />

          <DateSelector
            label="Data de término *"
            value={endDate}
            setValue={setEndDate}
            disablePast
            minDate={startDate}
            size="medium"
            helperText={
              endDateIsEarlierThanStart &&
              "Data de término deve ser posterior à data de início."
            }
          />
        </Box>

        <Box sx={sharedStyles.horizontalContent}>
          <TextField
            label="Mínimo de vagas *"
            size="small"
            type="number"
            fullWidth
            {...register("minVacancies")}
            error={!!errors.minVacancies}
            helperText={errors.minVacancies?.message}
            inputProps={{ min: 1 }}
            defaultValue={1}
          />
          <TextField
            label="Máximo de vagas *"
            size="small"
            type="number"
            fullWidth
            {...register("maxVacancies")}
            error={!!errors.maxVacancies}
            helperText={errors.maxVacancies?.message}
            inputProps={{ min: formValues.minVacancies }}
            defaultValue={10}
          />
        </Box>

        <Tiptap
          defaultValue={descriptionRef.current}
          onChange={(e) => (descriptionRef.current = e)}
          placeholder="Descreva os detalhes da excursão..."
        />

        <ExcursionRouter router={router} setRouter={setRouter} />

        {error && (
          <Alert severity="error" sx={{ marginY: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    </>
  );

  const renderExcursionDetails = (
    <>
      <Box sx={sharedStyles.verticalContent}>
        <TextField
          label="Serviço de transporte *"
          fullWidth
          {...register("transportService")}
          error={!!errors.transportService}
          helperText={errors.transportService?.message}
          defaultValue=""
          autoComplete="off"
        />
        <TextField
          label="Serviço de hospedagem *"
          fullWidth
          {...register("accommodationService")}
          error={!!errors.accommodationService}
          helperText={errors.accommodationService?.message}
          autoComplete="off"
        />

        <FormControl fullWidth>
          <InputLabel>Serviço de alimentação *</InputLabel>
          <Select
            label="Serviço de alimentação"
            defaultValue={FoodService[0]}
            {...register("foodService")}
            error={!!errors.foodService}
          >
            {FoodService.map((serviceType, index) => (
              <MenuItem value={FoodService[index]} key={index}>
                {serviceType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h6">Opcionais</Typography>

        <TextField
          label="Contrato da excursão"
          size="small"
          fullWidth
          {...register("excursionContract")}
          error={!!errors.excursionContract}
          helperText={errors.excursionContract?.message}
        />

        <TextField
          label="Link do grupo (Whatsapp)"
          size="small"
          fullWidth
          {...register("groupLink")}
          error={!!errors.groupLink}
          helperText={errors.groupLink?.message}
        />
      </Box>
    </>
  );

  const renderExcursionGeographicData = (
    <>
      <TextField
        label="Cidade *"
        size="small"
        fullWidth
        {...register("city")}
        error={!!errors.city}
        helperText={errors.city?.message}
        autoComplete="off"
      />
      <TextField
        label="Estado *"
        size="small"
        fullWidth
        {...register("state")}
        error={!!errors.state}
        helperText={errors.state?.message}
        autoComplete="off"
      />

      <TextField
        label="Região *"
        size="small"
        fullWidth
        {...register("region")}
        error={!!errors.region}
        helperText={errors.region?.message}
        autoComplete="off"
      />
      <TextField
        label="País *"
        size="small"
        fullWidth
        {...register("country")}
        error={!!errors.country}
        helperText={errors.country?.message}
        autoComplete="off"
      />
    </>
  );

  const renderExcursionImageGallery = (
    <Box sx={{ ...sharedStyles.verticalContent, marginY: 4 }}>
      <Box sx={{ alignSelf: "center", maxWidth: "400px" }}>
        <AddImageInput
          setImages={setImages}
          icon={<AddPhotoAlternateIcon color="secondary" />}
        />
      </Box>

      {images.length > 0 ? (
        <ImageGalleryContainer>
          <GalleryImageList images={images} setImages={setImages} interable />
        </ImageGalleryContainer>
      ) : (
        <EmptyGallery />
      )}
    </Box>
  );

  const renderAdditionSteps = () => {
    switch (additionStep) {
      case 0:
        return renderExcursionData;
      case 1:
        return renderExcursionDetails;
      case 2:
        return renderExcursionGeographicData;
      case 3:
        return renderExcursionImageGallery;
    }
  };

  const renderAddExcursionPageContent = (
    <>
      <LeftMarginBackButton />

      <Typography variant="h6" fontWeight="bold">
        Adição de excursão
      </Typography>

      <Stepper activeStep={additionStep} sx={{ width: "100%", marginY: 4 }}>
        {EXCURSION_OPERATION_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>
              <Typography
                sx={{ display: { xs: "none", md: "block", lg: "block" } }}
              >
                {label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <form onSubmit={onSubmit(handleSubmitAddition)} key={additionStep}>
        <Box sx={sharedStyles.verticalContent}>{renderAdditionSteps()}</Box>

        <Box sx={{ ...sharedStyles.dividedFormContainer, marginTop: 3 }}>
          <PrimaryButton
            label={isLastAdditionStep ? "Adicionar excursão" : "Confirmar"}
            styles={sharedStyles.secondaryColor}
            isLoading={additionLoading}
            icon={isLastAdditionStep && <AddIcon />}
          />

          {additionStep !== 0 && (
            <SecondaryButton
              label="Voltar"
              onClickAction={() =>
                handlePreviousStep(additionStep, setAdditionStep)
              }
            />
          )}
        </Box>
      </form>
    </>
  );

  return <ComercialDrawer content={renderAddExcursionPageContent} />;
};

export default AddExcursion;
